import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ShieldOff } from 'lucide-react';

import MemberDetailView from '../components/member-detail/MemberDetailView';
import { PERMISSIONS } from '../constants/permissions';
import { usePermission } from '../hooks/usePermission';
import FullPageFrame from '../components/shared/FullPageFrame';

/**
 * Full-page member detail (`members/:memberId/:tab?`). Gated by the members-view permission;
 * the body lives in `MemberDetailView`.
 *
 * @param {object} props
 * @param {string} props.basePath Where the host mounted People & Access (see `getBasePath`).
 * @param {boolean} [props.showDepartment] Overrides `SHOW_DEPARTMENT_FIELD`.
 */
function MemberDetailPage({ basePath, showDepartment }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const { memberId, tab } = useParams();
  const { allowed, loading } = usePermission(PERMISSIONS.MEMBERS_VIEW);
  const goBack = useCallback(() => navigate(`${basePath}/members`), [navigate, basePath]);

  if (loading) {
    return (
      <FullPageFrame>
        <Skeleton height="360px" borderRadius="12px" />
      </FullPageFrame>
    );
  }

  if (!allowed) {
    return (
      <FullPageFrame>
        <div className="flex flex-col items-center gap-3">
          <StatusMessage
            icon={ShieldOff}
            title={t('memberDetail.state.forbidden.title')}
            subtitle={t('memberDetail.state.forbidden.subtitle')}
          />
          <Button variant="secondary" size="md" onClick={goBack}>
            {t('memberDetail.state.backToList')}
          </Button>
        </div>
      </FullPageFrame>
    );
  }

  return (
    <MemberDetailView
      key={memberId}
      memberId={memberId}
      tab={tab}
      basePath={basePath}
      showDepartment={showDepartment}
    />
  );
}

export default MemberDetailPage;
